import { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/lib/utils';
import type { HotkeyNode } from '@/hooks/use-catalog-hotkeys';
import { useT } from '@/i18n/use-t';

/**
 * Sortable list of one parent's children. Drag emits onReorder with
 * the new full id-set (server requires exact match against the
 * current child-set; the consumer hook calls PATCH /reorder).
 *
 * H-122 — onReorder returns a promise; rejection reverts the local
 * order so the UI stops lying about server state. A parallel admin
 * who mutated the child set first will land first, the second
 * reorder fails (400 id-set mismatch), and the failing drag bounces
 * back visually.
 */
export function ChildList({
  parentId,
  // H-130: renamed from `children` (which shadows React's conventional
  // children slot) to `items`.
  items,
  selectedId,
  onSelect,
  onReorder,
  onAddChild,
}: {
  parentId: string | null;
  items: HotkeyNode[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onReorder: (parentId: string | null, orderedChildIds: string[]) => Promise<void>;
  onAddChild: (parentId: string | null) => void;
}) {
  const t = useT();
  const [order, setOrder] = useState<string[]>(items.map((c) => c.id));
  const [pending, setPending] = useState(false);
  // Keep local order in sync when server state changes from outside
  // (mutation invalidates the tree query).
  useEffect(() => {
    setOrder(items.map((c) => c.id));
  }, [items]);

  const byId = useMemo(() => new Map(items.map((c) => [c.id, c])), [items]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  async function handleDragEnd(event: DragEndEvent): Promise<void> {
    if (pending) return; // serialise rapid drags — H-122
    const { active, over } = event;
    if (over === null || active.id === over.id) return;
    const oldIndex = order.indexOf(String(active.id));
    const newIndex = order.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const prev = order;
    const next = [...order];
    next.splice(oldIndex, 1);
    next.splice(newIndex, 0, String(active.id));
    setOrder(next);
    setPending(true);
    try {
      await onReorder(parentId, next);
    } catch {
      setOrder(prev); // revert local optimism on failure
    } finally {
      setPending(false);
    }
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col gap-2 px-3 py-4">
        <p className="text-xs italic text-muted-foreground">
          {t('hotkeys.tree.noChildren', 'No children')}
        </p>
        <button
          type="button"
          onClick={() => onAddChild(parentId)}
          className="self-start rounded-md border border-dashed border-muted-foreground/40 px-3 py-1 text-xs text-muted-foreground hover:bg-muted/40"
        >
          {t('hotkeys.tree.addNode', '+ Add node')}
        </button>
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={order} strategy={verticalListSortingStrategy}>
        <ul className="flex flex-col gap-1 px-2 py-2">
          {order.map((id) => {
            const node = byId.get(id);
            if (node === undefined) return null;
            return (
              <SortableRow
                key={node.id}
                node={node}
                selected={node.id === selectedId}
                onSelect={onSelect}
              />
            );
          })}
          <li>
            <button
              type="button"
              onClick={() => onAddChild(parentId)}
              className="w-full rounded-md border border-dashed border-muted-foreground/40 px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted/40"
            >
              {t('hotkeys.tree.addNode', '+ Add node')}
            </button>
          </li>
        </ul>
      </SortableContext>
    </DndContext>
  );
}

function SortableRow({
  node,
  selected,
  onSelect,
}: {
  node: HotkeyNode;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: node.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => onSelect(node.id)}
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm transition-colors',
        selected
          ? 'border-primary bg-primary/10 text-primary-foreground'
          : 'border-border bg-card hover:bg-muted/40',
      )}
    >
      <span className="grid h-5 w-5 place-items-center text-[10px] font-semibold uppercase text-muted-foreground">
        {node.kind[0]}
      </span>
      <span className="flex-1 truncate">{node.label}</span>
      {node.iconName !== null && (
        <span className="text-[10px] text-muted-foreground">{node.iconName}</span>
      )}
    </li>
  );
}
